import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { healthCyclesAPI } from '../../services/api'
import { parseDiffPath, readableValue } from './cycleOverviewFormat'

const SUBJECT_LABELS = ['cycleReports.targetKey', 'cycleReports.limitCode', 'healthCycle.analysis.names', 'healthCycle.overview.field']

function ChangeLine({ change }) {
  const { t } = useTranslation()
  const { subjects } = parseDiffPath(change.path)
  const label = subjects
    .map((s) => s.key ?? s.selector)
    .map((token) => SUBJECT_LABELS.map((ns) => t(`${ns}.${token}`, '')).find(Boolean))
    .filter(Boolean)
    .join(' · ')
  const a = readableValue(change.a)
  const b = readableValue(change.b)
  const isScalar = (v) => v === null || v === undefined || typeof v !== 'object'
  const scalar = isScalar(change.a) && isScalar(change.b)
  return (
    <li className="break-words">
      <span className="text-gray-500">{label || t('healthCycle.compare.entry')}</span>:{' '}
      {scalar ? `${a} → ${b}` : change.a == null ? t('healthCycle.compare.added') : change.b == null ? t('healthCycle.compare.removed') : t('healthCycle.compare.changed')}
    </li>
  )
}

// VH-227: what changed between two versions of one cycle — field-level before/after per payload section.
export function CycleVersionCompare({ cycleId, versions = [] }) {
  const { t } = useTranslation()
  const numbers = versions.map((v) => v.number)
  const [a, setA] = useState(numbers[numbers.length - 2] ?? null)
  const [b, setB] = useState(numbers[numbers.length - 1] ?? null)
  const [requested, setRequested] = useState(null)

  const query = useQuery({
    queryKey: ['healthCycleCompare', cycleId, requested?.a, requested?.b],
    queryFn: () => healthCyclesAPI.compare(cycleId, requested.a, requested.b),
    select: (response) => response.data,
    enabled: !!requested,
  })

  const picker = (label, value, onChange, testId) => (
    <label className="text-sm text-gray-700 flex items-center gap-2">
      {label}
      <select className="text-sm" value={value ?? ''} onChange={(e) => onChange(Number(e.target.value))} data-testid={testId}>
        {numbers.map((n) => (
          <option key={n} value={n}>{t('healthCycle.versions.label', { number: n })}</option>
        ))}
      </select>
    </label>
  )

  const sections = Object.entries(query.data?.sections ?? {})
  return (
    <section className="card" aria-label={t('healthCycle.compare.title')} data-testid="cycle-version-compare">
      <div className="card-body space-y-3">
        <h3 className="text-sm font-semibold text-gray-900">{t('healthCycle.compare.title')}</h3>
        <div className="flex flex-wrap items-center gap-3">
          {picker(t('healthCycle.compare.from'), a, setA, 'compare-a')}
          {picker(t('healthCycle.compare.to'), b, setB, 'compare-b')}
          <button type="button" className="btn-outline text-sm" disabled={!a || !b || a === b} onClick={() => setRequested({ a, b })}>
            {t('healthCycle.compare.run')}
          </button>
        </div>
        {query.isFetching && <p className="text-sm text-gray-500">{t('common.loading')}</p>}
        {query.isError && <p className="text-sm text-red-600" role="alert">{t('healthCycle.compare.error')}</p>}
        {query.data && !query.isFetching && (
          <div className="space-y-2 text-sm">
            {sections.length === 0 && <p className="text-gray-500">{t('healthCycle.compare.noDifferences')}</p>}
            {sections.map(([name, changes]) => (
              <div key={name} data-testid={`compare-section-${name}`}>
                <p className="font-medium text-gray-900">{t(`healthCycle.sections.${name}`, t('healthCycle.compare.otherSection'))}</p>
                <ul className="text-xs text-gray-700 space-y-0.5 overflow-x-auto">
                  {changes.map((c, i) => <ChangeLine key={`${c.path}-${i}`} change={c} />)}
                </ul>
              </div>
            ))}
            {query.data.reports_changed && <p className="text-xs text-amber-700">{t('healthCycle.compare.reportsChanged')}</p>}
          </div>
        )}
      </div>
    </section>
  )
}

export default CycleVersionCompare
